import { AnalyticsEventNames } from './types';

const DEFAULT_DEBOUNCE_TIME = 500;
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

export interface AnalyticsItem {
  init: (userId: string) => void;
  trackEvent: (name: string, parameters?: Record<string, any>) => void;
  setUserProperties: (properties: Record<string, any>) => void;
  setUserId: (id: string) => void;
}

class AnalyticManager {
  private subscribers: AnalyticsItem[] = [];
  private userId = '';
  private isInitialized = false;
  private debouncedEventName: string | null | undefined;
  private debounceTimer: null | number | undefined;

  constructor(initialSubscribers: AnalyticsItem[] = []) {
    initialSubscribers.forEach((subscriber) => this.addSubscriber(subscriber));
  }

  init(data: {
    userId: string;
    userProperties: Record<string, any>;
    onInitEvent?: { name: string; params?: Record<string, any> };
  }): void {
    this.userId = data.userId;

    this.subscribers.forEach((subscriber) => {
      subscriber.init(this.userId);
      subscriber.setUserProperties(data.userProperties);
    });

    this.isInitialized = true;

    if (IS_DEVELOPMENT) {
      console.warn('update analytics user id: ', this.userId);
    }

    if (data.onInitEvent) {
      this.subscribers.forEach((subscriber) => {
        if (data.onInitEvent) {
          subscriber.trackEvent(data.onInitEvent.name, data.onInitEvent?.params);
        }
      });
    }
  }

  addSubscriber(subscriber: AnalyticsItem): void {
    this.subscribers.push(subscriber);

    if (this.isInitialized) {
      subscriber.init(this.userId);
    }
  }

  setUserId(id: string): void {
    this.userId = id;

    this.subscribers.forEach((subscriber) => {
      subscriber.setUserId(id);
    });
  }

  setUserProperties(properties: Record<string, any>): void {
    if (!this.isInitialized) {
      return;
    }

    this.subscribers.forEach((subscriber) => {
      subscriber.setUserProperties(properties);
    });
  }

  trackEvent(name: AnalyticsEventNames, parameters?: Record<string, any>, skipDebounce = false): void {
    if (!this.isInitialized) {
      return;
    }

    if (!skipDebounce && this.debouncedEventName === name) {
      return;
    }

    this.debouncedEventName = name as string;

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(() => {
      this.debouncedEventName = null;
    }, DEFAULT_DEBOUNCE_TIME);

    this.subscribers.forEach((subscriber) => {
      subscriber.trackEvent(name, parameters);
    });
  }
}

const analyticsManager = new AnalyticManager();

export { analyticsManager };
